import { ACCOUNT_ONLY_PERMISSIONS, AppRoute, MULTI_SITE_PERMISSIONS, ROUTE_PERMISSIONS } from '../models/navigation.model';
import { PermissionAction } from '../permissions/permissions.model';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PowerBiReportConfig } from '@weavix/models/src/powerbi/report-config';
import { debounceTime, ReplaySubject, Subject, Subscription } from 'rxjs';
import { Dashboard } from '../models/dashboard.model';
import { Facility } from '../models/facility.model';
import { FontelloIcon } from '../models/icon.model';
import { TreeItem } from '../models/tree-item.model';
import { Utils } from '../utils/utils';
import { AccountService } from './account.service';
import { FacilityService } from './facility.service';
import { ProfileService } from './profile.service';
import { TranslationService } from './translation.service';
import { PowerBiService } from './powerbi.service';
import { Person } from 'weavix-shared/models/person.model';
import { PersonService } from './person.service';
import { matchPerson } from '@weavix/domain/src/people/match-person';
import { PersonLike } from '@weavix/models/src/person/person-matcher';
import { CustomDictionaryService } from './custom-dictionary.service';
import { FeatureFlagService } from './feature-flag.service';
import { FeatureId } from '@weavix/models/src/features/feature';

export interface NavigationItem extends TreeItem {
    route?: AppRoute | string;
    activeRoutes?: Array<AppRoute | string>;
    children?: NavigationItem[];
}

@Injectable({
    providedIn: 'root',
})
export class NavigationService {

    public navigationItems: NavigationItem[];
    public navigationItems$ = new ReplaySubject<NavigationItem[]>(1);
    public expanded: boolean;
    private currentFacility: string;
    private currentPerson: Person;
    public navigationExpanded = new Subject<void>();
    /** The flags version the items were last built from; see {@link watchFlags}. */
    private builtWithFlags?: string;
    private flagsSubscription?: Subscription;

    public localDashboards: Dashboard[];
    public powerBiReports: PowerBiReportConfig[];

    constructor(
        private readonly router: Router,
        private readonly profileService: ProfileService,
        private readonly facilityService: FacilityService,
        private readonly accountService: AccountService,
        private readonly translationService: TranslationService,
        private readonly powerBiService: PowerBiService,
        private readonly personService: PersonService,
        private readonly featureFlagService: FeatureFlagService,
        private readonly customDictionaryService: CustomDictionaryService,
    ) {
        this.expanded = localStorage.getItem('navigation-expanded') !== 'false';
        this.facilityService.getCurrentFacilityId$().subscribe(id => {
            const fixChildren = item => {
                if (item?.url) item.url = item.url.replace(this.currentFacility, id);
                if (item?.children) item.children.forEach(fixChildren);
            };
            fixChildren({ children: this.navigationItems });
            this.currentFacility = id;
            this.setupNavigationItems();
        });
    }

    async setupNavigationItems() {
        this.watchFlags();
        this.builtWithFlags = this.featureFlagService.getFlagsVersion();
        this.currentPerson = await this.personService.getPerson(null, this.profileService.userProfile.id, true);
        this.currentFacility = this.facilityService.getCurrentFacilityId() ?? this.facilityService.getLastFacilityIdFromStorage();
        const facilities = await this.facilityService.getMyFacilitiesOnCurrentAccount(null);
        if (!facilities.some(x => x.id === this.currentFacility)) this.currentFacility = facilities[0]?.id;
        const siteVisionUrl: string = `f/${this.currentFacility}`;
        if (this.profileService.hasAccountOrAnyFolderPermission(PermissionAction.AdministerAccount) && !this.powerBiReports) {
            await this.loadPowerBiReports();
        }
        const peopleRouteChildren = [
            {
                route: AppRoute.LiveLook,
                activeRoutes: [AppRoute.LiveLook],
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.LiveLook]),
                titleKey: 'console-navigation.live-look',
                url: `/${siteVisionUrl}/${AppRoute.People}/${AppRoute.LiveLook}`,
            },
            {
                route: AppRoute.WorkerEfficiency,
                activeRoutes: [AppRoute.WorkerEfficiency],
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.WorkerEfficiency]),
                titleKey: 'console-navigation.worker-efficiency',
                url: `/${siteVisionUrl}/${AppRoute.People}/${AppRoute.WorkerEfficiency}`,
            },
            {
                route: AppRoute.LocationDiagnostics,
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.LocationDiagnostics]),
                activeRoutes: [AppRoute.LocationDiagnostics],
                titleKey: 'console-navigation.location-diagnostics',
                url: `/${siteVisionUrl}/${AppRoute.People}/${AppRoute.LocationDiagnostics}`,
            },
            ...this.getDynamicRouteItems(AppRoute.People),
            ...this.getDynamicRouteItems(AppRoute.Equipment),
        ];
        const smartRadioRouteChildren = this.getDynamicRouteItems(AppRoute.SmartRadio);
        const safetyRouteChildren = [
            {
                route: AppRoute.Alerts,
                activeRoutes: [AppRoute.Alerts],
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.Alerts]),
                titleKey: 'console-navigation.alerts',
                url: `/${siteVisionUrl}/${AppRoute.Safety}/${AppRoute.Alerts}`,
            },
            {
                route: AppRoute.SafetyObservations,
                activeRoutes: [AppRoute.SafetyObservations],
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.SafetyObservations]) || !await this.hasFeature(FeatureId.SafetyObservation) || !await this.isSafetyObservationFeatureEnabled(),
                titleKey: 'console-navigation.safety-observations',
                url: `/${siteVisionUrl}/${AppRoute.Safety}/${AppRoute.SafetyObservations}`,
                isBeta: true,
            },
            ...this.getDynamicRouteItems(AppRoute.Safety),
        ];
        const configurationRouteChildren = [
            {
                route: AppRoute.AlertTypes,
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.AlertTypes]),
                activeRoutes: [AppRoute.AlertTypes],
                titleKey: 'console-navigation.alert-types',
                url: `/${AppRoute.Configuration}/${AppRoute.AlertTypes}`,
            },
            {
                route: AppRoute.General,
                activeRoutes: [AppRoute.General],
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.General]),
                titleKey: 'console-navigation.account',
                url: `/${AppRoute.Configuration}`,
            },
            {
                route: AppRoute.Channels,
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.Channels]),
                activeRoutes: [AppRoute.Channels],
                titleKey: 'console-navigation.channels',
                url: `/${AppRoute.Configuration}/${AppRoute.Channels}`,
            },
            {
                route: AppRoute.DeviceManagement,
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.DeviceManagement]),
                activeRoutes: [AppRoute.DeviceManagement],
                titleKey: 'console-navigation.device-management',
                url: `/${AppRoute.Configuration}/${AppRoute.DeviceManagement}`,
            },
            {
                route: AppRoute.DeviceNetworkStatus,
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.DeviceNetworkStatus]),
                activeRoutes: [AppRoute.DeviceNetworkStatus],
                titleKey: 'console-navigation.device-network-status',
                url: `/${AppRoute.Configuration}/${AppRoute.DeviceNetworkStatus}`,
                isBeta: true,
            },
            {
                route: AppRoute.ApiKeys,
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.ApiKeys]),
                activeRoutes: [AppRoute.ApiKeys],
                titleKey: 'console-navigation.api-keys',
                url: `/${AppRoute.Configuration}/${AppRoute.ApiKeys}`,
            },
            {
                route: AppRoute.Compliance,
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.Compliance]),
                activeRoutes: [AppRoute.Compliance],
                titleKey: 'console-navigation.compliance',
                url: `/${AppRoute.Configuration}/${AppRoute.Compliance}`,
            },
            {
                route: AppRoute.Crafts,
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.Crafts]),
                activeRoutes: [AppRoute.Crafts],
                titleKey: 'console-navigation.crafts',
                url: `/${AppRoute.Configuration}/${AppRoute.Crafts}`,
            },
            {
                route: AppRoute.CustomDictionary,
                // Retiring: the site-and-language dictionary on the site page replaces it, so this
                // account-wide page goes away once every site has it, or once its list is empty.
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.CustomDictionary])
                    || await this.hasSiteDictionariesEverywhere(facilities)
                    || !(await this.hasAccountWideTerms()),
                activeRoutes: [AppRoute.CustomDictionary],
                titleKey: 'console-navigation.custom-dictionary',
                url: `/${AppRoute.Configuration}/${AppRoute.CustomDictionary}`,
            },
            {
                route: AppRoute.Dashboards,
                hidden: !this.profileService.isGlobalAdmin(),
                activeRoutes: [AppRoute.Dashboards],
                titleKey: 'console-navigation.dashboards',
                url: `/${AppRoute.Configuration}/${AppRoute.Dashboards}`,
            },
            {
                route: AppRoute.Forms,
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.Forms]),
                activeRoutes: [AppRoute.Forms],
                titleKey: 'console-navigation.forms',
                url: `/${AppRoute.Configuration}/${AppRoute.Forms}`,
            },
            {
                route: AppRoute.GeofenceTypes,
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.GeofenceTypes]),
                activeRoutes: [AppRoute.GeofenceTypes],
                titleKey: 'console-navigation.geofence-types',
                url: `/${AppRoute.Configuration}/${AppRoute.GeofenceTypes}`,
            },
            {
                route: AppRoute.ItemTypes,
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.ItemTypes]),
                activeRoutes: [AppRoute.ItemTypes],
                titleKey: 'console-navigation.item-types',
                url: `/${AppRoute.Configuration}/${AppRoute.ItemTypes}`,
            },
            {
                route: AppRoute.PowerBiConfig,
                hidden: !this.profileService.isGlobalAdmin() || !this.hasAccountPermission(PermissionAction.PowerBiReports),
                activeRoutes: [AppRoute.PowerBiConfig],
                titleKey: 'generics.reports',
                url: `/${AppRoute.Configuration}/${AppRoute.PowerBiConfig}`,
            },
            {
                route: AppRoute.Sites,
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.Sites]),
                activeRoutes: [AppRoute.Sites],
                titleKey: 'console-navigation.sites',
                url: `/${AppRoute.Configuration}/${AppRoute.Sites}`,
            },
            {
                route: AppRoute.Users,
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.Users]),
                activeRoutes: [AppRoute.Users],
                titleKey: 'console-navigation.users',
                url: `/${AppRoute.Configuration}/${AppRoute.Users}`,
            },
            {
                route: AppRoute.Workflows,
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.Workflows]),
                activeRoutes: [AppRoute.Workflows],
                titleKey: 'console-navigation.workflows',
                url: `/${AppRoute.Configuration}/${AppRoute.Workflows}`,
            },
            ...this.getDynamicRouteItems(AppRoute.Configuration),
        ];

        [...peopleRouteChildren,
         ...smartRadioRouteChildren,
         ...safetyRouteChildren,
         ...configurationRouteChildren].forEach(c => {
            c.originalUrl = c.url;
        });

        this.navigationItems = [
            {
                route: AppRoute.People,
                activeRoutes: [AppRoute.People, AppRoute.LiveLook, AppRoute.WorkerEfficiency, ...this.getDynamicRoutes(AppRoute.People), ...this.getDynamicRoutes(AppRoute.Equipment)],
                hidden: !peopleRouteChildren?.some(c => !c.hidden) || !facilities?.length,
                main: true,
                icon: { faIcon: 'fas fa-user-hard-hat' },
                titleKey: 'console-navigation.people',
                children: [
                    ...peopleRouteChildren,
                ],
            },
            {
                route: AppRoute.SmartRadio,
                activeRoutes: [AppRoute.SmartRadio, ...this.getDynamicRoutes(AppRoute.SmartRadio)],
                hidden: !facilities?.length || !smartRadioRouteChildren.length,
                main: true,
                icon: { fntlIcon: FontelloIcon.Walt },
                titleKey: 'console-navigation.smart-radio',
                children: [
                    ...smartRadioRouteChildren,
                ],
            },
            {
                route: AppRoute.Safety,
                activeRoutes: [AppRoute.Alerts, AppRoute.SafetyObservations, ...this.getDynamicRoutes(AppRoute.Safety)],
                hidden: !safetyRouteChildren?.some(c => !c.hidden) || !facilities?.length,
                main: true,
                icon: { faIcon: 'fas fa-shield-alt' },
                titleKey: 'console-navigation.safety',
                children: [
                    ...safetyRouteChildren,
                ],
            },
            {
                route: AppRoute.Configuration,
                activeRoutes: this.getConfigurationActiveRoutes(),
                hidden: !configurationRouteChildren?.some(c => !c.hidden),
                main: true,
                icon: { faIcon: 'fas fa-cog' },
                titleKey: 'shared.icons.configuration',
                children: [
                    ...configurationRouteChildren,
                ],
            },
        ];

        this.navigationItems.forEach(n => {
            Utils.sortAlphabetical(n.children, (c) => this.translationService.getImmediate(c.titleKey));
        });
        this.navigationItems$.next(this.navigationItems);
    }

    public setupLocalDashboards(dashboards: Dashboard[]) {
        this.localDashboards = dashboards.filter(d => d.enabled);
    }

    public async loadPowerBiReports() {
        this.powerBiReports = await this.powerBiService.getAll(null) ?? [];
    }

    private getDynamicRoutes(route: AppRoute): string[] {
        return this.getDashboardRoutes(route)
            .concat(this.getPowerBiRoutes(route));
    }

    private getDashboardRoutes(route: AppRoute): string[] {
        return this.localDashboards?.filter(d => d.navParent === route).map(d => d.id) ?? [];
    }

    private getPowerBiRoutes(route: AppRoute): string[] {
        return (this.powerBiReports || [])
            .filter(report => report.navParent === route)
            .map(report => report.id);
    }

    private getDynamicRouteItems(route: AppRoute): NavigationItem[] {
        return this.getDashboardRouteItems(route)
            .concat(this.getPowerBiReportRouteItems(route));
    }

    private getDashboardRouteItems(route: AppRoute): NavigationItem[] {
        const matchData: PersonLike = {
            ...this.currentPerson,
            facilityIds: Object.keys(this.currentPerson.facilities),
        };

        return (this.localDashboards ?? [])
            .filter(d => d.navParent === route)
            .filter(d => {
                if (matchData.supportAccess) return true; // always show if support access is active
                return matchPerson(d, matchData, (f) => matchData.facilityIds.includes(f));
            })
            .map(d => {
                return {
                    route: d.id,
                    activeRoutes: [AppRoute.Dashboard, d.id],
                    hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.Dashboards]),
                    titleKey: d.name,
                    url: `${this.getDynamicRouteUrlBase(route)}/${AppRoute.Dashboard}/${d.id}`,
                };
        }) ?? [];
    }

    private getPowerBiReportRouteItems(route: AppRoute): NavigationItem[] {
        return (this.powerBiReports ?? [])
            .filter(report => report.navParent === route)
            .map(report => ({
                route: report.id,
                activeRoutes: [AppRoute.PowerBiReport, report.id],
                hidden: this.hideRoute(ROUTE_PERMISSIONS[AppRoute.PowerBiReport]) || !this.hasAccountPermission(PermissionAction.PowerBiReports),
                titleKey: report.name,
                url: `${this.getDynamicRouteUrlBase(route)}/${AppRoute.PowerBiReport}/${report.id}`,
            }));
    }

    private getDynamicRouteUrlBase(route: AppRoute): string {
        if (route === AppRoute.Configuration) {
            return `/${route}`;
        } else {
          return `/f/${this.currentFacility}/${route}`;
        }
    }

    private getConfigurationActiveRoutes() {
        return [
            AppRoute.Configuration, AppRoute.General, AppRoute.Channels, AppRoute.ApiKeys, AppRoute.Crafts, AppRoute.CustomDictionary, AppRoute.Dashboards,
            AppRoute.Forms, AppRoute.GeofenceTypes, AppRoute.ItemTypes, AppRoute.Sites, AppRoute.Users, AppRoute.Workflows, AppRoute.AlertTypes,
            ...this.getDynamicRoutes(AppRoute.Configuration),
        ];
    }

    public toggleExpansion(): void {
        this.navigationItems.forEach(navItem => {
            navItem.dropdown = false;
        });
        this.expanded = !this.expanded;
        localStorage.setItem('navigation-expanded', `${this.expanded}`);
        this.navigationExpanded.next();
    }

    public toggleNavItem(item): void {
        item.closed = !item.closed;
    }

    public toggleNavItemDropdown(item: NavigationItem): void {
        this.navigationItems.forEach(navItem => {
            if (navItem.titleKey === item.titleKey) {
                item.dropdown = !item.dropdown;
            } else {
                navItem.dropdown = false;
            }
        });
    }

    public navigateAndToggleNavItemDropdown(item: NavigationItem, parent: NavigationItem): void {
        parent.dropdown = false;
        this.router.navigateByUrl(item.url);
    }

    public getFirstAvailableItemUrl(): string {
        const firstAvailable = this.getAllVisibleItems()[0];
        return `${this.accountService.getAccountRoute()}/${firstAvailable?.url}`;
    }

    public isUrlAvailable(url: string): boolean {
        return this.getAllVisibleItems()
            .some(itemOrChildItem => itemOrChildItem.url === url);
    }

    private getAllVisibleItems(): NavigationItem[] {
        return this.navigationItems
            .filter(item => !item.hidden)
            .flatMap(item => item.children ?? [item])
            .filter(itemOrChildItem => !itemOrChildItem.hidden);
    }

    public hasFacilityRoutePermissions(navItemPerms: PermissionAction[], anyRoute = false): boolean {
        return !navItemPerms?.length ? true : navItemPerms?.some(p => {
            if (ACCOUNT_ONLY_PERMISSIONS.includes(p)) return this.profileService.hasAccountOrAnyFolderPermission(p);
            if (MULTI_SITE_PERMISSIONS.includes(p) || anyRoute) return this.profileService.hasAnyPermissionInAnyFacility(p);
            return this.profileService.hasAnyPermission(p);
        });
    }

    /**
     * Rebuilds the items when flags change after they were built. Items read their flags once, and right
     * after sign-in the flags sync in after the first build. Subscribed once, on the first build.
     */
    private watchFlags() {
        this.flagsSubscription ??= this.featureFlagService.getFlagsVersion$()
            .pipe(debounceTime(500))
            .subscribe(version => {
                if (version === this.builtWithFlags) return;
                this.setupNavigationItems().catch((err: unknown) => {
                    console.error('Rebuilding the navigation after a feature flag change failed', err);
                });
            });
    }

    private hideRoute(perms: PermissionAction[]): boolean {
        return !this.hasFacilityRoutePermissions(perms, true);
    }

    private hasAccountPermission(perm: PermissionAction): boolean {
        return this.profileService.hasAccountPermission(perm);
    }

    private async hasFeature(feature: FeatureId): Promise<boolean> {
        return await this.featureFlagService.isFeatureFlagEnabled(feature);
    }

    /**
     * Whether every site this user can see has AI transcription on (an account-wide flag counts), the
     * site dictionary replacing this page there. No sites, or a failed read, leaves the page showing.
     */
    private async hasSiteDictionariesEverywhere(facilities: Facility[]): Promise<boolean> {
        if (!facilities?.length) return false;
        try {
            const accountId = this.accountService.getAccountId();
            const enabled = await Promise.all(facilities.map(facility =>
                this.featureFlagService.isFacilityFeatureFlagEnabled(FeatureId.MicrosoftAiTranscription, accountId, facility.id)));
            return enabled.every(Boolean);
        } catch (e) {
            console.error('Reading the AI transcription flag for the Custom Dictionary nav item failed', e);
            return false;
        }
    }

    /**
     * Whether the account-wide dictionary still holds anything; empty hides the page even where sites
     * lack the flag. A failure leaves the page showing.
     */
    private async hasAccountWideTerms(): Promise<boolean> {
        try {
            return !!(await this.customDictionaryService.getAll(this))?.length;
        } catch {
            return true;
        }
    }

    private async isSafetyObservationFeatureEnabled(): Promise<boolean> {
        return await this.featureFlagService.isFeatureFlagEnabled(FeatureId.SafetyObservation) &&
            this.profileService.hasAccountPermission(PermissionAction.ViewSafetyObservation);
    }
}
